import {
  Box,
  withStyles,
  TransactionsLists,
  removeWhiteSpaceURI,
  capitalizeFirstLetter,
  Tooltip,
  UtilButton,
  SupportedCurrencies,
  formattedCurrency,
} from 'vopay';
import { FunctionComponent, useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useClientData } from '@/providers/ClientData.provider';
import { usePermissions } from '@/providers/Permissions.provider';
import useFetchTransactionsHistory from '@/hooks/useApi/useFetchTransactionsHistory';
import { format, parse, addDays, subWeeks } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useNavMenu } from '@/providers/NavMenu.provider';
import TransactionStatus from '@/components/UI/TransactionStatus/TransactionStatus';
import Table from '../../components/Table';
import { formatInTimeZone } from 'date-fns-tz';
import styles from './RecentTransactions.module.scss';
import NoRecentTransactions from './NoRecentTransactions';
import { Title, TransactionTypeLabel } from 'UI';
import Throbber from '../../components/Throbber';

const cn = withStyles(styles);

type RecentTransactionsProps = {
  currency: SupportedCurrencies;
};

type FC = FunctionComponent<RecentTransactionsProps>;

const RecentTransactions: FC = ({ currency }) => {
  const { response } = useClientData();
  const { t, i18n } = useTranslation('translations', { keyPrefix: 'HomepageRevamp.Widgets.RecentTransactions' });
  const { t: tGlobal } = useTranslation('translations');
  const { fetching, fetchTransactionsHistory } = useFetchTransactionsHistory();
  const [transactionsData, setTransactionsData] = useState<TransactionsLists[]>([]);
  const { onSubNavClick } = useNavMenu();
  const { permissions } = usePermissions();

  useEffect(() => {
    if (!permissions.ViewTransactionHistory) return;

    const fetchData = async () => {
      const startDateTime = formatInTimeZone(subWeeks(new Date(), 1), 'UTC', 'yyyy-MM-dd HH:mm:ss');
      const currentDateTime = formatInTimeZone(addDays(new Date(), 1), 'UTC', 'yyyy-MM-dd HH:mm:ss');

      try {
        const res = await fetchTransactionsHistory({
          PortalUser: response.LoginUserName,
          StartDateTime: startDateTime,
          EndDateTime: currentDateTime,
          Currency: currency,
          Limit: 5,
        });
        setTransactionsData(Object.values(res.Transactions || {}));
      } catch (err) {
        console.error(err);
      }
    };
    fetchData();
  }, [permissions.ViewTransactionHistory, response.LoginUserName, currency]);

  const columns = [
    { columnKey: 'recent-id', title: t('id') },
    { columnKey: 'recent-type', title: t('type') },
    { columnKey: 'recent-date', title: t('date') },
    { columnKey: 'recent-amount', title: t('amount') },
    { columnKey: 'recent-status', title: t('status') },
  ];

  const rows = useMemo(() => {
    return transactionsData.map((data) => ({
      rowKey: data.TransactionID,
      [t('id')]: (
        <Box gap="sm" ai="center">
          <Link
            className={styles.transactionIDLink}
            to={{
              pathname: `/transactions-history/details/${removeWhiteSpaceURI(data.TransactionType)}/${
                data.TransactionID
              }/${data.Currency}`,
              state: {
                transactionId: data.TransactionID,
                hasErrorInfo: data.TransactionStatus === 'failed',
                failureReason: data.TransactionFailureReason,
                clientAccountId: data.ClientAccountID,
              },
            }}
          >
            {data.TransactionID}
          </Link>
        </Box>
      ),
      [t('type')]: <TransactionTypeLabel transactionType={data.TransactionType} />,
      [t('date')]: format(parse(data.TransactionDateTime, 'yyyy-MM-dd HH:mm:ss', new Date()), 'MMM dd, yyyy', {
        locale: i18n.language === 'fr' ? fr : undefined,
      }),
      [t('amount')]:
        data.DebitAmount !== '0.00'
          ? `${currency} ${formattedCurrency(data.DebitAmount)}`
          : `${currency} ${formattedCurrency(data.CreditAmount)}`,
      [t('status')]: (
        <Box gapSize="10px">
          <TransactionStatus
            status={data.TransactionStatus}
            label={
              capitalizeFirstLetter(
                tGlobal(`Transaction.TransactionHistory.statuses.${data.TransactionStatus}`, {
                  defaultValue: data.TransactionStatus,
                }),
              ) || ''
            }
          />
          {data.TransactionFailureReason && (
            <Tooltip
              information={data.TransactionFailureReason}
              icon="info"
              variant="outlined"
              iconWidth="14px"
              buttonWidth="22px"
              hasButton={true}
              position="bottomLeft"
            />
          )}
        </Box>
      ),
    }));
  }, [transactionsData, currency, t, tGlobal]);

  return (
    <>
      <div className={cn('recentTransactionHeader')}>
        <Title level="5" style={{ alignSelf: 'flex-start' }}>
          {t('title')}
        </Title>
      </div>

      {!permissions.ViewTransactionHistory ? (
        <div className={cn('emptyState')}>
          {t('noPermission', { defaultValue: `You don't have permission to view transaction history.` })}
        </div>
      ) : fetching ? (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <Throbber />
        </div>
      ) : rows.length === 0 ? (
        <NoRecentTransactions />
      ) : (
        <Table
          id="recentTransaction"
          columns={columns}
          rows={rows}
          cellPadding="sm"
          bodyHeight="calc(100% - 50px)"
          cellWidth="190px"
        />
      )}

      {permissions.ViewTransactionHistory && (
        <div className={cn('buttonContainer')}>
          <UtilButton
            size="large"
            onClick={() => {
              onSubNavClick(
                'transactionsHistory',
                `/transactions-history?currency=${currency}`,
                t('transactionsHistory'),
              );
            }}
          >
            {t('viewAllTransactions')}
          </UtilButton>
        </div>
      )}
    </>
  );
};

export default RecentTransactions;
